import React, { useRef, useEffect } from "react";
import { X, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const ImagePreviewModal = ({
  previewImage,
  setPreviewImage,
  previewItemCode,
  previewItemId,
  items,
  setItems
}) => {
  const imageUploadRef = useRef(null);

  // Handle paste event for image
  useEffect(() => {
    if (!previewImage) return;

    const handlePaste = async (e) => {
      const clipboardItems = e.clipboardData?.items;
      if (!clipboardItems) return;

      for (const item of clipboardItems) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
              const newImageData = event.target.result;
              const newItems = items.map((itm, idx) => {
                if (itm.id === previewItemId || idx === previewItemId) {
                  return { ...itm, image_data: newImageData };
                }
                return itm;
              });
              setItems(newItems);
              setPreviewImage(newImageData);
              toast.success(`Imagen pegada para ${previewItemCode}`);
            };
            reader.readAsDataURL(file);
          }
          break;
        }
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [previewImage, previewItemId, previewItemCode, items, setItems, setPreviewImage]);

  // Handle escape key
  useEffect(() => {
    if (!previewImage) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setPreviewImage(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [previewImage, setPreviewImage]);

  if (!previewImage) return null;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const newImageData = event.target.result;
        const newItems = items.map((item, idx) => {
          if (item.id === previewItemId || idx === previewItemId) {
            return { ...item, image_data: newImageData };
          }
          return item;
        });
        setItems(newItems);
        setPreviewImage(newImageData);
        toast.success(`Imagen actualizada para ${previewItemCode}`);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
      onClick={() => setPreviewImage(null)}
    >
      <div 
        className="relative bg-white rounded-lg shadow-2xl max-w-3xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-3 border-b bg-slate-50">
          <span className="text-sm font-medium text-slate-700">
            {previewItemCode}
          </span>
          <div className="flex items-center gap-2">
            <input
              ref={imageUploadRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
              data-testid="change-image-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => imageUploadRef.current?.click()}
              className="text-xs"
              data-testid="change-image-btn"
            >
              <Upload className="w-3 h-3 mr-1" />
              Cambiar Imagen
            </Button>
            <button
              onClick={() => setPreviewImage(null)}
              className="p-1 hover:bg-slate-200 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>
        <div className="p-4 bg-white flex items-center justify-center">
          <img
            src={previewImage}
            alt={previewItemCode}
            className="max-w-full max-h-[70vh] object-contain"
          />
        </div>
        <div className="p-2 border-t bg-slate-50 text-center">
          <span className="text-xs text-slate-500">
            Presiona <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-slate-700 font-mono">Ctrl+V</kbd> para pegar imagen | <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-slate-700 font-mono">Esc</kbd> para cerrar
          </span>
        </div>
      </div>
    </div>
  );
};
